import { useEffect, useRef, useState } from 'react';
import { TextInput } from 'components/common/form/common';

import type { TextInputProps } from 'components/common/form/common/TextInput';

type Props = TextInputProps & {
  onValueChange: (value: string) => void;
  value: string;
};

/**
 * Wrapper for text input component to be used in editor. Handles deferred
 * updates and keeping the input value in sync with the passed value.
 *
 * @param onValueChange - Change handler for text field value
 * @param value - Value of the text field
 * @param otherProps - Other props supported by TextInput
 */
const EditorBlockTextField = ({
  onValueChange,
  value,
  ...otherProps
}: Props) => {
  const [inputValue, setInputValue] = useState(value || '');

  const updateTimeout = useRef<NodeJS.Timeout>(undefined);

  useEffect(() => {
    return () => clearTimeout(updateTimeout.current);
  }, []);

  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  const handleChange = (ev) => {
    const newInputValue = ev.target.value as string;

    setInputValue(newInputValue);

    clearTimeout(updateTimeout.current);

    updateTimeout.current = setTimeout(() => {
      onValueChange(newInputValue);
    }, 500);
  };

  return (
    <TextInput {...otherProps} onChange={handleChange} value={inputValue} />
  );
};

export default EditorBlockTextField;
